import React from "react";
import { Info } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Pill } from "@admin/components/Primitives";

// UI-only grouping of the three independent lawyer flags. Reads `verified`,
// `listing_approved` and `available` exactly as stored — no derived field.
export const VLA_LEGEND = "VLA = Verification, Listing, Availability";

export function vlaRows(lawyer) {
  return [
    { key: "verification", letter: "V", label: "Verification",
      on: !!lawyer.verified, color: lawyer.verified ? "emerald" : "amber",
      text: lawyer.verified ? `Verified${lawyer.verification_source === "admin_override" ? " (override)" : ""}` : "Not verified" },
    { key: "listing", letter: "L", label: "Listing",
      on: !!lawyer.listing_approved, color: lawyer.listing_approved ? "blue" : "slate",
      text: lawyer.listing_approved ? "Listed" : "Unlisted" },
    { key: "availability", letter: "A", label: "Availability",
      on: !!lawyer.available, color: lawyer.available ? "emerald" : "slate",
      text: lawyer.available ? "Online" : "Offline" },
  ];
}

export function VlaHeader() {
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button type="button" data-testid="vla-header" aria-label={`VLA status. ${VLA_LEGEND}`}
                  className="inline-flex items-center gap-1 uppercase tracking-wider font-semibold text-xs text-slate-500 hover:text-[#0A2540] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D4AF37] rounded">
            VLA Status <Info size={12} aria-hidden="true" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" className="bg-[#0A2540] text-white normal-case tracking-normal" data-testid="vla-header-tooltip">
          <p className="font-bold">{VLA_LEGEND}</p>
          <p className="mt-1 font-normal text-slate-200">V = Verification · L = Marketplace Listing · A = Availability</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// Inline tags: one pill per letter, laid out like the action buttons.
export function VlaStatus({ lawyer }) {
  const rows = vlaRows(lawyer);
  const status = lawyer.account_status || "active";
  const summary = rows.map((r) => `${r.label}: ${r.text}`).join(", ");
  return (
    <div className="flex flex-wrap items-center gap-1" data-testid={`vla-status-${lawyer.id}`} role="group" aria-label={`VLA status — ${summary}`}>
      {rows.map((r) => (
        <span key={r.key} data-testid={`vla-${r.key}-${lawyer.id}`} data-state={r.on ? "on" : "off"} title={`${r.label}: ${r.text}`}>
          <Pill color={r.color}>{r.text}</Pill>
        </span>
      ))}
      {status !== "active" && (
        <span data-testid={`vla-account-${lawyer.id}`} title={`Account: ${status}`}>
          <Pill color={status === "disabled" ? "amber" : "rose"}>{status}</Pill>
        </span>
      )}
    </div>
  );
}
